import React from 'react';

import * as messages from '@radnuc/messages';
import * as d3 from 'd3';
import {Config} from './Config';
import { t } from 'i18next';
import './i18n';
import {checkSnoozeState} from './SnoozeIsotopeDisplay'
import strong_lines_list from './spectrum_strong_lines_RFG';

import './SpectrumDisplay.css'

type strong_lines = {name: string, name_hyphen: string, lines: number[]}
type GammaSpectrum = { time: number, sum: number, spectrum: number[], livetime: number }
type EnergyCountPair = { energy: number, count: number }
type NeutronData = { time: number, count: number, livetime: number }

export enum positionType {
    BEARING = 1,
    LANE = 2,
    IN_LANE = 3,
    NONE = 4
}

export interface SpectrumDisplayProperty {
    gammaInLanePosition: number | undefined,
    neutronInLanePosition: number | undefined,
}
export interface SpectrumDisplayInterface {
    inLanePositionInit: SpectrumDisplayProperty,
    updateInLanePosition: (position: SpectrumDisplayProperty) => void,
}

export interface SpectrumDisplayProps {
    radiationData: messages.Rad.RadiationReading[] | null,
    neutronData: messages.Rad.NeutronReading[] | null,
    pcsResults: messages.Rad.PCSResult[] | null,
    liveStream: boolean,
    replayTime?: Date,
    isPrimary?: boolean,
    lanePosition?: number,
    multiLane?: boolean,
    isDualStanchion?: boolean,
    showByPositionType?: positionType,
    spectrumDisplayInterface? : SpectrumDisplayInterface,
    config?: Config,
}

export interface SpectrumDisplayState {
    plotTimeRange: [number, number] | undefined
    liveSpectrumSumStartTime: number
}

export class SpectrumDisplay extends React.Component<SpectrumDisplayProps, SpectrumDisplayState> {
    spectra: GammaSpectrum[];
    sourceSpectrum: EnergyCountPair[];
    timestamps: number[];
    counts: number[][];
    neutronCounts: NeutronData[];
    min_channel_rate: number = 5e-3;
    energy_scale: number[];
    //plotTimeRange: [number,number] | undefined;
    lastTimeForceRedraw: number = 0;
    dominantIsotope: string | null = null;
    //gamma_max_in_lane_pos: number | undefined;
    gamma_possible_in_lane_pos: number[] = [];
    //neutron_max_in_lane_pos: number | undefined;
    neutron_possible_in_lane_pos: number[] = [];
    gammaInLanePosition?: number;
    neutronInLanePosition?: number;
    possible_lane_pos: number[] = [];
    lanePosition?: number;

    constructor(props: SpectrumDisplayProps) {
        super(props)

        this.state = {
            plotTimeRange: undefined,
            liveSpectrumSumStartTime: 0
        }
        this.spectra = [];
        this.counts = [];
        this.neutronCounts = [];
        this.timestamps = []
        this.energy_scale = [];
        for (var i = 0; i < 1024; ++i) {
            this.energy_scale.push(3000 / 1024.0 / 2 + 3000 / 1024.0 * i);
        }
        this.sourceSpectrum = [];

        this.setSpectrumSumRange = this.setSpectrumSumRange.bind(this);
        this.setLiveSpectrumSumStartTime = this.setLiveSpectrumSumStartTime.bind(this);

        this.calcGammaSpectrumAndCount = this.calcGammaSpectrumAndCount.bind(this);
    }

    componentDidUpdate(prevProps: SpectrumDisplayProps) {
        //if (this.props.radiationData !== prevProps.radiationData) {
            this.calcGammaSpectrumAndCount();
            this.calcNeutronCount();
        //}
        if(this.props.spectrumDisplayInterface !== undefined){
            this.gammaInLanePosition = this.props.spectrumDisplayInterface.inLanePositionInit.gammaInLanePosition;
            this.neutronInLanePosition = this.props.spectrumDisplayInterface.inLanePositionInit.neutronInLanePosition;
        }
    }

    addVector(a: number[], b: number[] | null) {
        if (b !== null)
            return a.map((e, i) => e + b[i]);
        else
            return a;
    }

    sum_spectra(start_time: number, end_time: number) {
        var spectra = this.spectra.filter(function (d: any) {
            return d.time > start_time && d.time < end_time;
        });

        var sum_spectrum = new Array(1024).fill(0.0);
        var livetime = 0.0;
        spectra.forEach(function (spec) {
            if (spec.spectrum !== null && spec.spectrum !== undefined) {
                spec.spectrum.forEach(function (cnt, idx) {
                    sum_spectrum[idx] += cnt;
                });
            }
            livetime += spec.livetime;
        });

        var energy_sum_spectrum: EnergyCountPair[] = [];
        this.min_channel_rate = 1 / spectra.length;
        sum_spectrum.forEach((count, idx) => {
            //This sets the floor of the spectrum plot with livetime accounted
            if (count < this.min_channel_rate * livetime)
                count = this.min_channel_rate * livetime;

            energy_sum_spectrum.push(
                { energy: this.energy_scale[idx], count: count / livetime });
        }, this);

        return energy_sum_spectrum;
    }

    setSpectrumSumRange(start_time: number | null, end_time: number | null) {
        const timenow = Date.now();
        if (timenow > this.lastTimeForceRedraw + 50) {
            if (start_time !== null && end_time !== null) {
                if (start_time <= end_time) {
                    this.setState(() => ({
                        plotTimeRange: [start_time, end_time]
                    }));
                    //console.log("start time: " + start_time.valueOf() + " end time " + end_time.valueOf());
                } else {
                    this.setState(() => ({
                        plotTimeRange: undefined
                    }));
                }
            } else {
                this.setState(() => ({
                    plotTimeRange: undefined
                }));
            }
            this.lastTimeForceRedraw = timenow;
        }
    }

    setLiveSpectrumSumStartTime(start_time: number){
        const timenow = Date.now();
        if (timenow > this.lastTimeForceRedraw + 50) {
            this.setState(() => ({
                liveSpectrumSumStartTime: start_time
            }));
            this.lastTimeForceRedraw = timenow;
        }
    }

    findDominantIsotope(time0: number | null, time1: number | null, source_lane?: number, source_lane_position?: number, showByPositionType?:positionType, combinedHorizontally?:boolean){
        var dominant_isotope = null;
        if(this.props.pcsResults != null){
            var pcs_results:messages.Rad.PCSResult[] | null = null;
            if(time0 !== null && time1 !== null){
                pcs_results = this.props.pcsResults.filter(function(d){
                  return d.time > time0 && d.time < time1;
                });
            } else {
                pcs_results = [this.props.pcsResults[this.props.pcsResults.length-1]];

                //Optional Snooze Isotope
                if(this.props.config){
                    if(checkSnoozeState(pcs_results[pcs_results.length-1],this.props.config)){
                        pcs_results = [];
                    }
                }
            }
            dominant_isotope = this.searchDominantIsotopeInArray(pcs_results,source_lane,source_lane_position,showByPositionType,combinedHorizontally);
        }
        return dominant_isotope;
    }

    searchDominantIsotopeInArray(pcs_results:messages.Rad.PCSResult[],source_lane?:number, source_lane_position?: number, showByPositionType?:positionType, combinedHorizontally?:boolean){
      var dominant_isotope = null;
      var max_isotope = 0;
      pcs_results.forEach(function(pcs_result) {
          if(pcs_result != null){
            if(source_lane === undefined){
                if(showByPositionType !== undefined){
                    if(source_lane_position === undefined){
                        pcs_result.pcs_result.forEach(function(isotope) {
                            if (isotope.pcs_confidence > max_isotope && isotope.alarm) {
                                max_isotope = isotope.pcs_confidence;
                                dominant_isotope = isotope.name;
                            }
                        });
                    } else {
                        const angularPosLUT:{[key:number]:number} = {90:1,270:0};
                        let detectorPosition:number|undefined;
                        if(showByPositionType === positionType.BEARING){
                            detectorPosition = angularPosLUT[pcs_result.pcs_alpha_source_bearing];
                        } else if(showByPositionType === positionType.IN_LANE){
                            detectorPosition = pcs_result.source_lane_position;
                        } else if(showByPositionType === positionType.LANE){
                            detectorPosition = pcs_result.source_lane;
                        }
                        if(detectorPosition === source_lane_position){
                            pcs_result.pcs_result.forEach(function(isotope) {
                                if (isotope.pcs_confidence > max_isotope && isotope.alarm) {
                                    max_isotope = isotope.pcs_confidence;
                                    dominant_isotope = isotope.name;
                                }
                            });
                        }
                    }
                } else {
                    //All lanes
                    pcs_result.pcs_result.forEach(function(isotope) {
                        if (isotope.pcs_confidence > max_isotope && isotope.alarm) {
                            max_isotope = isotope.pcs_confidence;
                            dominant_isotope = isotope.name;
                        }
                    });
                }
            } else {
                if(pcs_result.source_lane === source_lane){
                    if(source_lane_position === undefined){
                        pcs_result.pcs_result.forEach(function(isotope) {
                            if (isotope.pcs_confidence > max_isotope && isotope.alarm) {
                                max_isotope = isotope.pcs_confidence;
                                dominant_isotope = isotope.name;
                            }
                        });
                    } else {
                        let equivalent_source_lane_position = source_lane_position;
                        if(combinedHorizontally){
                            if(equivalent_source_lane_position > 1){
                                equivalent_source_lane_position = equivalent_source_lane_position-2;
                            }
                        }
                        if(pcs_result.source_lane_position === equivalent_source_lane_position){
                            pcs_result.pcs_result.forEach(function(isotope) {
                                if (isotope.pcs_confidence > max_isotope && isotope.alarm) {
                                    max_isotope = isotope.pcs_confidence;
                                    dominant_isotope = isotope.name;
                                }
                            });
                        }
                    }
                }
            }
          }
      });
      return dominant_isotope;
    }

    calcGammaSpectrumAndCount() {
        //Calculates count sum and average spectrum of all detectors
        this.spectra = [];
        this.counts = [];
        var livetime = 0.0;
        var sum_spectrum: number | null = 0.0;
        this.gamma_possible_in_lane_pos = [];
        this.possible_lane_pos = [];
        if (this.props.radiationData !== null && this.props.radiationData.length > 0) {
            this.props.radiationData.forEach((data) => {
                //if (data.time > time_now - 120000) {
                var this_sum_spectrum = 0.0;
                livetime = 0;
                var spectrum: number[] | null = new Array(1024).fill(0.0);
                //let max_in_lane_pos = 0;
                data.detectors.forEach((det) => {
                    if(this.props.lanePosition !== undefined && this.props.isPrimary === true){
                        //Primary and in lane specific view
                        if(det.lane_position === this.props.lanePosition){
                            if(this.gammaInLanePosition !== undefined){
                                if(det.in_lane_position === this.gammaInLanePosition){
                                    spectrum = this.addVector(det.spectrum, spectrum);
                                    this_sum_spectrum += d3.sum(det.spectrum);
                                    livetime += det.livetime;
                                }
                            } else {
                                spectrum = this.addVector(det.spectrum, spectrum);
                                this_sum_spectrum += d3.sum(det.spectrum);
                                livetime += det.livetime;
                            }
                            if(this.gamma_possible_in_lane_pos.length > 0){
                                const idx = this.gamma_possible_in_lane_pos.find((pos) => pos === det.in_lane_position);
                                if(idx === undefined){
                                    this.gamma_possible_in_lane_pos.push(det.in_lane_position);
                                }
                            } else {
                                this.gamma_possible_in_lane_pos.push(det.in_lane_position);
                            }
                        }
                    } else if(this.props.isPrimary === false){
                        //Secondary
                        if(this.gammaInLanePosition !== undefined){
                            if(det.in_lane_position === this.gammaInLanePosition){
                                spectrum = this.addVector(det.spectrum, spectrum);
                                this_sum_spectrum += d3.sum(det.spectrum);
                                livetime += det.livetime;
                            }
                        } else {
                            spectrum = this.addVector(det.spectrum, spectrum);
                            this_sum_spectrum += d3.sum(det.spectrum);
                            livetime += det.livetime;
                        }
                        //max_in_lane_pos = max_in_lane_pos<det.in_lane_position ? det.in_lane_position : max_in_lane_pos
                        if(this.gamma_possible_in_lane_pos.length > 0){
                            const idx = this.gamma_possible_in_lane_pos.find((pos) => pos === det.in_lane_position);
                            if(idx === undefined){
                                this.gamma_possible_in_lane_pos.push(det.in_lane_position);
                            }
                        } else {
                            this.gamma_possible_in_lane_pos.push(det.in_lane_position);
                        }
                    } else if(this.props.multiLane === true){
                        //Multi Lane display
                        if(det.lane_position === this.lanePosition){
                            //console.log(this.lanePosition)
                            if(this.gammaInLanePosition !== undefined){
                                if(det.in_lane_position === this.gammaInLanePosition){
                                    spectrum = this.addVector(det.spectrum, spectrum);
                                    this_sum_spectrum += d3.sum(det.spectrum);
                                    livetime += det.livetime;
                                }
                            } else {
                                spectrum = this.addVector(det.spectrum, spectrum);
                                this_sum_spectrum += d3.sum(det.spectrum);
                                livetime += det.livetime;
                            }
                            //Find possible in-lane positions
                            if(this.gamma_possible_in_lane_pos.length > 0){
                                const idx = this.gamma_possible_in_lane_pos.find((pos) => pos === det.in_lane_position);
                                if(idx === undefined){
                                    this.gamma_possible_in_lane_pos.push(det.in_lane_position);
                                }
                            } else {
                                this.gamma_possible_in_lane_pos.push(det.in_lane_position);
                            }
                        } else if(this.lanePosition === undefined){
                            //console.log('lanePosition undefined'+this.lanePosition)
                            //All lane positions
                            spectrum = this.addVector(det.spectrum, spectrum);
                            this_sum_spectrum += d3.sum(det.spectrum);
                            livetime += det.livetime;
                        }
                        //Find possible lane positions in the data
                        if(this.possible_lane_pos.length > 0){
                            const idx = this.possible_lane_pos.find((pos) => pos === det.lane_position);
                            if(idx === undefined){
                                this.possible_lane_pos.push(det.lane_position);
                            }
                        } else {
                            this.possible_lane_pos.push(det.lane_position);
                        }
                    } else if(this.props.showByPositionType !== undefined){
                        //show by position ()
                        const angularPosLUT:{[key:number]:number} = {90:1,270:0};
                        let detectorPosition:number|undefined;
                        if(this.props.showByPositionType === positionType.BEARING){
                            detectorPosition = angularPosLUT[det.angular_position];
                        } else if(this.props.showByPositionType === positionType.IN_LANE){
                            detectorPosition = det.in_lane_position;
                        } else if(this.props.showByPositionType === positionType.LANE){
                            detectorPosition = det.lane_position;
                        }

                        if(this.gammaInLanePosition !== undefined){
                            if(detectorPosition === this.gammaInLanePosition){
                                spectrum = this.addVector(det.spectrum, spectrum);
                                this_sum_spectrum += d3.sum(det.spectrum);
                                livetime += det.livetime;
                            }
                        } else {
                            //ALL
                            spectrum = this.addVector(det.spectrum, spectrum);
                            this_sum_spectrum += d3.sum(det.spectrum);
                            livetime += det.livetime;
                        }
                        //Find possible in-lane positions
                        if(detectorPosition !== undefined){
                            if(this.gamma_possible_in_lane_pos.length > 0){
                                const idx = this.gamma_possible_in_lane_pos.find((pos) => pos === detectorPosition);
                                if(idx === undefined){
                                    this.gamma_possible_in_lane_pos.push(detectorPosition);
                                }
                            } else {
                                this.gamma_possible_in_lane_pos.push(detectorPosition);
                            }
                        }
                    } else {
                        //Average of all (Default)
                        spectrum = this.addVector(det.spectrum, spectrum);
                        this_sum_spectrum += d3.sum(det.spectrum);
                        livetime += det.livetime;
                    }
                });


                sum_spectrum = this_sum_spectrum;

                if (livetime === 0) {
                    livetime = 1;
                    spectrum = new Array(1024).fill(0);
                    sum_spectrum = 0;
                    //this.gamma_max_in_lane_pos = undefined;
                } else {
                    if(this.props.isPrimary !== undefined){
                        if(this.props.isPrimary){
                            //Primary
                            //this.gamma_max_in_lane_pos = (this.props.lanePosition !==undefined) ? max_in_lane_pos : undefined;
                            //this.gamma_possible_in_lane_pos = (this.props.lanePosition !==undefined) ? this.gamma_possible_in_lane_pos : [];
                            if(this.props.lanePosition ===undefined)
                                this.gamma_possible_in_lane_pos = [];
                        } else if(this.props.multiLane){
                            if(this.lanePosition ===undefined)
                                this.gamma_possible_in_lane_pos = [];
                        } else {
                            //Secondary
                            //this.gamma_max_in_lane_pos = max_in_lane_pos;
                        }
                    } else {
                        //this.gamma_max_in_lane_pos = undefined;
                    }
                }

                if (data.time !== null) {
                    var result = {
                        time: data.time,
                        sum: sum_spectrum,
                        spectrum: spectrum,
                        livetime: livetime,
                    };
                    this.spectra.push(result);

                    //if (sum_spectrum === 0) {
                    //    console.log('sum_spectrum == 0');
                    //}
                }

                this.counts.push([data.time, this_sum_spectrum]);
                //}
            });
        }
        this.spectra.sort(function (x, y) { return d3.ascending(x.time, y.time); });

        if (this.spectra.length > 3) {
            if (this.state.plotTimeRange === undefined) {
                //No time range selected
                if(this.props.liveStream === false){
                    //If viewing replay data, don't show dominant if there's no timerange and also clear dominant isotope
                    this.dominantIsotope = null;
                    this.sourceSpectrum = this.sum_spectra(this.spectra[0].time, this.spectra[this.spectra.length - 1].time);
                } else {
                    //If viewing live data, show only current dominant isotope
                    if(this.props.multiLane){
                        this.dominantIsotope = this.findDominantIsotope(null,null,this.lanePosition,this.gammaInLanePosition);
                    } else if (this.props.showByPositionType){
                        this.dominantIsotope = this.findDominantIsotope(null,null,undefined,this.gammaInLanePosition,this.props.showByPositionType);
                    } else {
                        if(this.props.isDualStanchion){
                            //Dual stanchion reports pcs inlane_position of 0 (lower) or 1(upper)
                            this.dominantIsotope = this.findDominantIsotope(null,null,this.props.lanePosition,this.gammaInLanePosition,undefined,true);
                        } else {
                            this.dominantIsotope = this.findDominantIsotope(null,null,this.props.lanePosition,this.gammaInLanePosition);
                        }
                    }
                    this.sourceSpectrum = this.sum_spectra(this.state.liveSpectrumSumStartTime, this.spectra[this.spectra.length - 1].time);
                }
            } else {
                //time range selected
                if(this.state.plotTimeRange[1] > this.state.liveSpectrumSumStartTime) {
                    //If there is a plot timerange, show dominant in the timerange
                    const start_time = Math.max(this.state.liveSpectrumSumStartTime,this.state.plotTimeRange[0]);
                    this.sourceSpectrum = this.sum_spectra(start_time, this.state.plotTimeRange[1]);
                    if(this.props.multiLane){
                        this.dominantIsotope = this.findDominantIsotope(start_time, this.state.plotTimeRange[1],this.lanePosition,this.gammaInLanePosition);
                    } else if (this.props.showByPositionType){
                        this.dominantIsotope = this.findDominantIsotope(start_time,this.state.plotTimeRange[1],undefined,this.gammaInLanePosition,this.props.showByPositionType);
                    } else {
                        if(this.props.isDualStanchion){
                            //Dual stanchion reports pcs inlane_position of 0 (lower) or 1(upper)
                            this.dominantIsotope = this.findDominantIsotope(start_time, this.state.plotTimeRange[1],this.props.lanePosition,this.gammaInLanePosition,undefined,true);
                        } else {
                            this.dominantIsotope = this.findDominantIsotope(start_time, this.state.plotTimeRange[1],this.props.lanePosition,this.gammaInLanePosition);
                        }
                    }
                } else {
                    //Time range is invalid now (scrolled off)
                    this.setSpectrumSumRange(null, null);
                    if(this.props.liveStream === false){
                        //Replay
                        this.sourceSpectrum = this.sum_spectra(this.spectra[0].time, this.spectra[this.spectra.length - 1].time);
                    } else {
                        this.sourceSpectrum = this.sum_spectra(this.state.liveSpectrumSumStartTime, this.spectra[this.spectra.length - 1].time);
                        //If viewing live data, show only current
                        if(this.props.multiLane){
                            this.dominantIsotope = this.findDominantIsotope(null,null,this.lanePosition,this.gammaInLanePosition);
                        } else if (this.props.showByPositionType){
                            this.dominantIsotope = this.findDominantIsotope(null,null,undefined,this.gammaInLanePosition,this.props.showByPositionType);
                        } else {
                            if(this.props.isDualStanchion){
                                    //Dual stanchion reports pcs inlane_position of 0 (lower) or 1(upper)
                                this.dominantIsotope = this.findDominantIsotope(null,null,this.props.lanePosition,this.gammaInLanePosition,undefined,true);
                            } else {
                                this.dominantIsotope = this.findDominantIsotope(null,null,this.props.lanePosition,this.gammaInLanePosition);
                            }
                        }
                    }
                }
            }
        } else {
            this.sourceSpectrum = new Array(1024).fill(0.0);
            this.dominantIsotope = null;
        }
    }

    calcNeutronCount(){
        this.neutronCounts = [];
        let livetime = 0.0;
        this.neutron_possible_in_lane_pos = [];
        if (this.props.neutronData !== null && this.props.neutronData.length > 0) {
            this.props.neutronData.forEach((data) => {
                var this_sum_count = 0.0;
                livetime = 0;
                if(data.detectors !== undefined){
                    //let max_in_lane_pos = 0;
                    data.detectors.forEach((det) => {
                        if(this.props.lanePosition !== undefined && this.props.isPrimary === true){
                            if(det.lane_position === this.props.lanePosition){
                                //Primary and lane specific view
                                if(this.neutronInLanePosition !== undefined){
                                    if(det.in_lane_position === this.neutronInLanePosition){
                                        this_sum_count += det.count;
                                        livetime += det.livetime;
                                    }
                                } else {
                                    this_sum_count += det.count;
                                    livetime += det.livetime;
                                }
                                //max_in_lane_pos = (max_in_lane_pos < det.in_lane_position) ? det.in_lane_position : max_in_lane_pos
                                if(this.neutron_possible_in_lane_pos.length > 0){
                                    const idx = this.neutron_possible_in_lane_pos.find(pos => pos === det.in_lane_position);
                                    if(idx === undefined){
                                        this.neutron_possible_in_lane_pos.push(det.in_lane_position);
                                    }
                                } else {
                                    this.neutron_possible_in_lane_pos.push(det.in_lane_position);
                                }
                            }
                        } else if(this.props.isPrimary === false){
                            //Secondary
                            if(this.neutronInLanePosition !== undefined){
                                if(det.in_lane_position === this.neutronInLanePosition){
                                    this_sum_count += det.count;
                                    livetime += det.livetime;
                                }
                            } else {
                                this_sum_count += det.count;
                                livetime += det.livetime;
                            }
                            //max_in_lane_pos = (max_in_lane_pos < det.in_lane_position) ? det.in_lane_position : max_in_lane_pos
                            if(this.neutron_possible_in_lane_pos.length > 0){
                                const idx = this.neutron_possible_in_lane_pos.find(pos => pos === det.in_lane_position);
                                if(idx === undefined){
                                    this.neutron_possible_in_lane_pos.push(det.in_lane_position);
                                }
                            } else {
                                this.neutron_possible_in_lane_pos.push(det.in_lane_position);
                            }
                        } else if(this.props.multiLane === true){
                            if(det.lane_position === this.lanePosition){
                                //MultiLane view
                                if(this.neutronInLanePosition !== undefined){
                                    if(det.in_lane_position === this.neutronInLanePosition){
                                        this_sum_count += det.count;
                                        livetime += det.livetime;
                                    }
                                } else {
                                    this_sum_count += det.count;
                                    livetime += det.livetime;
                                }
                                if(this.neutron_possible_in_lane_pos.length > 0){
                                    const idx = this.neutron_possible_in_lane_pos.find(pos => pos === det.in_lane_position);
                                    if(idx === undefined){
                                        this.neutron_possible_in_lane_pos.push(det.in_lane_position);
                                    }
                                } else {
                                    this.neutron_possible_in_lane_pos.push(det.in_lane_position);
                                }
                            } else if(this.lanePosition === undefined){
                                //Average of all (Default)
                                this_sum_count += det.count;
                                livetime += det.livetime;
                            }
                            if(this.possible_lane_pos.length > 0){
                                const idx = this.possible_lane_pos.find(pos => pos === det.lane_position);
                                if(idx === undefined){
                                    this.possible_lane_pos.push(det.lane_position);
                                }
                            } else {
                                this.possible_lane_pos.push(det.lane_position);
                            }
                        } else if(this.props.showByPositionType !== undefined){
                            //show by position ()
                            const angularPosLUT:{[key:number]:number} = {90:1,270:0};
                            let detectorPosition:number|undefined;
                            if(this.props.showByPositionType === positionType.BEARING){
                                detectorPosition = angularPosLUT[det.angular_position];
                            } else if(this.props.showByPositionType === positionType.IN_LANE){
                                detectorPosition = det.in_lane_position;
                            } else if(this.props.showByPositionType === positionType.LANE){
                                detectorPosition = det.lane_position;
                            }

                            if(this.neutronInLanePosition !== undefined){
                                if(detectorPosition === this.neutronInLanePosition){
                                    this_sum_count += det.count;
                                    livetime += det.livetime;
                                }
                            } else {
                                this_sum_count += det.count;
                                livetime += det.livetime;
                            }
                            //Find possible in-lane positions
                            if(detectorPosition !== undefined){
                                if(this.neutron_possible_in_lane_pos.length > 0){
                                    const idx = this.neutron_possible_in_lane_pos.find((pos) => pos === detectorPosition);
                                    if(idx === undefined){
                                        this.neutron_possible_in_lane_pos.push(detectorPosition);
                                    }
                                } else {
                                    this.neutron_possible_in_lane_pos.push(detectorPosition);
                                }
                            }
                        } else {
                            //Average of all (Default)
                            this_sum_count += det.count;
                            livetime += det.livetime;
                        }
                    });

                    if(livetime ===0){
                        this_sum_count=0;
                        livetime = 1;
                        //this.neutron_max_in_lane_pos = undefined;
                    } else {
                        if(this.props.isPrimary !== undefined){
                            if(this.props.isPrimary){
                                //Primary
                                //this.neutron_max_in_lane_pos = (this.props.lanePosition!==undefined) ? max_in_lane_pos : undefined;
                                if(this.props.lanePosition ===undefined)
                                    this.neutron_possible_in_lane_pos = [];
                            } else {
                                //Secondary
                                //this.neutron_max_in_lane_pos = max_in_lane_pos;
                            }
                        } else {
                            //this.neutron_max_in_lane_pos = undefined;
                        }
                    }
                    const result = {
                        time: data.time,
                        count: this_sum_count,
                        livetime: livetime,
                    };
                    this.neutronCounts.push(result);
                }
            });
        }

    };

    handleGammaDetSelection(selection?:number){
            this.gammaInLanePosition = selection;
    }
    handleNeutronDetSelection(selection?:number){
            this.neutronInLanePosition = selection;
    }

    makeDetectorSelectDisplay(){
        let detectorSelectDisplay;
        let gammaButtons:JSX.Element[] = [];
        //if(this.gamma_max_in_lane_pos!==undefined){
        //    for(var i=0; i<=this.gamma_max_in_lane_pos;i++){
        if(this.gamma_possible_in_lane_pos.length > 0){
            this.gamma_possible_in_lane_pos.forEach((i)=>{
                const inLanePos = i;
                let thisSelectedClass;
                if(this.gammaInLanePosition === inLanePos )
                    thisSelectedClass = 'selected';
                const this_button =
                    <div key={inLanePos} className={"tiny-button "+thisSelectedClass}
                                onClick={() => this.handleGammaDetSelection(inLanePos)}>{inLanePos}</div>
                gammaButtons.push(this_button)
            });
            let allSelectedClass;
            if(this.gammaInLanePosition === undefined)
                allSelectedClass = 'selected';
            const all_button =
                <div key={100} className={"tiny-button "+allSelectedClass}
                            onClick={() => this.handleGammaDetSelection()}>All</div>
            gammaButtons.push(all_button)
        }

        let neutronButtons:JSX.Element[] = [];
        //if(this.neutron_max_in_lane_pos!==undefined){
        //    for(var i=0; i<=this.neutron_max_in_lane_pos;i++){
        if(this.neutron_possible_in_lane_pos.length > 0){
            this.neutron_possible_in_lane_pos.forEach((i)=>{
                const inLanePos = i;
                let thisSelectedClass;
                if(this.neutronInLanePosition === inLanePos )
                    thisSelectedClass = 'nselected';
                const this_button =
                    <div key={inLanePos} className={"tiny-button "+thisSelectedClass}
                                onClick={() => this.handleNeutronDetSelection(inLanePos)}>{inLanePos}</div>
                neutronButtons.push(this_button)
            });
            let allSelectedClass;
            if(this.neutronInLanePosition === undefined)
                allSelectedClass = 'nselected';
            const all_button =
                <div key={100} className={"tiny-button "+allSelectedClass}
                            onClick={() => this.handleNeutronDetSelection()}>All</div>
            neutronButtons.push(all_button)
        }
        detectorSelectDisplay =
            <div id='detector-select-container' style={{ display: 'flex', flexDirection: 'row', justifyContent:'flex-end', marginRight:'5px'}}>
                <span style={{color:'olivedrab',marginRight:'10px'}}>Gamma Detector Position</span>{gammaButtons}
                <div style={{marginRight:'20px'}}/>
                <span style={{color:'steelblue',marginRight:'10px'}}>Neutron Detector Position</span>{neutronButtons}
            </div>
        return(
            <div style={{position: 'relative'}}>
            {detectorSelectDisplay}
            </div>
        )

    }
    handleLaneSelection(selection?:number){
            this.lanePosition = selection;
    }
    makeLaneSelectDisplay(){
        let laneSelectDisplay;
        let laneButtons:JSX.Element[] = [];
        if(this.possible_lane_pos.length > 0){
            this.possible_lane_pos.forEach((i)=>{
                const lanePos = i;
                let thisSelectedClass;
                if(this.lanePosition === lanePos )
                    thisSelectedClass = 'selected';
                const this_button =
                    <div key={lanePos} className={"tiny-button "+thisSelectedClass}
                                onClick={() => this.handleLaneSelection(lanePos)}>{lanePos}</div>
                laneButtons.push(this_button)
            });
            let allSelectedClass;
            if(this.lanePosition === undefined)
                allSelectedClass = 'selected';
            const all_button =
                <div key={100} className={"tiny-button "+allSelectedClass}
                            onClick={() => this.handleLaneSelection()}>All</div>
            laneButtons.push(all_button)
        }

        laneSelectDisplay =
            <div id='detector-select-container' style={{ display: 'flex', flexDirection: 'row', justifyContent:'flex-end', marginRight:'5px'}}>
                <span style={{color:'white',marginRight:'10px'}}>Position</span>{laneButtons}
            </div>
        return(
            <div style={{position: 'relative'}}>
            {laneSelectDisplay}
            </div>
        )

    }

    handlePositionSelection(selection?:number){
            this.gammaInLanePosition = selection;
            this.neutronInLanePosition = selection;
            if(this.props.spectrumDisplayInterface){
                const spectrumDisplayPositions:SpectrumDisplayProperty = {gammaInLanePosition:this.gammaInLanePosition,
                                                                          neutronInLanePosition:this.neutronInLanePosition}
                this.props.spectrumDisplayInterface.updateInLanePosition(spectrumDisplayPositions);
            }
            /*
            if(this.neutron_possible_in_lane_pos.length > 1){
                this.neutronInLanePosition = selection;
            } else {
                this.neutronInLanePosition = undefined;
            }
            */
    }
    makePositionSelectDisplay(){
        let positionSelectDisplay;
        let positionButtons:JSX.Element[] = [];
        let positionNameLUT:{[position:number]:string}|undefined;
        if(this.props.showByPositionType === positionType.BEARING){
            positionNameLUT = {0:'L',1:'R'}
        } else if(this.props.showByPositionType === positionType.IN_LANE){
            positionNameLUT = {0:'B',1:'T'}
        } else {
            positionNameLUT = undefined;
        }

        const combined_possible_in_lane_pos = Array.prototype.concat(this.gamma_possible_in_lane_pos,this.neutron_possible_in_lane_pos).filter((v, i, a) => a.indexOf(v) === i);;
        if(combined_possible_in_lane_pos.length > 0 ){
            combined_possible_in_lane_pos.sort((a,b) => a-b).forEach((i)=>{
                const lanePos = i;
                let lanePosString;
                if(lanePos !== null){
                    lanePosString = lanePos.toFixed(0);
                }
                if(positionNameLUT !== undefined){
                    lanePosString = t(positionNameLUT[lanePos],{ns:'trans'});
                }
                let thisSelectedClass;
                if(this.gammaInLanePosition === lanePos )
                    thisSelectedClass = 'selected';
                const this_button =
                    <div key={lanePos} className={"tiny-button "+thisSelectedClass}
                                onClick={() => this.handlePositionSelection(lanePos)}>{lanePosString}</div>
                if(lanePos !== null)
                    positionButtons.push(this_button)
            });
            let allSelectedClass;
            if(this.gammaInLanePosition === undefined)
                allSelectedClass = 'selected';
            const all_button =
                <div key={100} className={"tiny-button "+allSelectedClass}
                            onClick={() => this.handlePositionSelection()}>{t('All',{ns:'trans'})}</div>
            positionButtons.push(all_button)
        }

        positionSelectDisplay =
            <div id='detector-select-container' style={{ display: 'flex', flexDirection: 'row', justifyContent:'flex-end', marginRight:'5px'}}>
                <span style={{color:'white',marginRight:'10px'}}>{t('Position',{ns:'trans'})}</span>{positionButtons}
            </div>
        return(
            <div style={{position: 'relative'}}>
            {positionSelectDisplay}
            </div>
        )

    }

    render() {
        //console.log('Render Spectrum @'+Date.now())
        let detectorSelectDisplay; // =  this.makeDetectorSelectDisplay();
        if(this.props.isPrimary !== undefined){
            if(this.props.isPrimary){
                //Primary
                if(this.props.lanePosition !== undefined){
                    detectorSelectDisplay =  this.makeDetectorSelectDisplay();
                }
            } else {
                //Secondary
                detectorSelectDisplay =  this.makeDetectorSelectDisplay();
            }
        }
        let laneSelectDisplay;
        if(this.props.multiLane){
            laneSelectDisplay = this.makeLaneSelectDisplay();
        }
        let positionSelectDisplay;
        if(this.props.showByPositionType){
            positionSelectDisplay = this.makePositionSelectDisplay();
        }

        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {laneSelectDisplay}
                {detectorSelectDisplay}
                {positionSelectDisplay}
                <MemoizedSpectrumPlot data={this.sourceSpectrum} title={'Gamma Spectrum'} dominantIsotope={this.dominantIsotope}/>
                <MemoizedCountHistoryPlot data={this.spectra} neutronData={this.neutronCounts} replayTime={this.props.replayTime} liveStream={this.props.liveStream}
                    onSelectRange={this.setSpectrumSumRange}  setStartTime={this.setLiveSpectrumSumStartTime}/>
            </div>
        );
    }

}
export default SpectrumDisplay;


//******************************************************************************
export interface CountHistoryPlotProps {
    data: GammaSpectrum[],
    neutronData: NeutronData[] | null,
    replayTime?: Date,
    liveStream: boolean,
    onSelectRange: (start_time: number | null, end_time: number | null) => void;
    setStartTime: (start_time: number) => void;
}

export interface CountHistoryPlotState {
    counthistory_log_scale: boolean,
}

export class CountHistoryPlot extends React.Component<CountHistoryPlotProps,CountHistoryPlotState> {
    base: HTMLDivElement | null
    updateRef: (element: HTMLDivElement) => void
    previous_brush: [number, number] | undefined;

    plot_margin = { top: 20, right: 55, bottom: 30, left: 55 };
    count_rate_x: d3.ScaleTime<number, number> | null = null;
    count_rate_y: d3.ScaleContinuousNumeric<number, number> | null = null;
    count_rate_g: any | null
    neutron_count_rate_x: d3.ScaleTime<number, number> | null = null;
    neutron_count_rate_y: d3.ScaleContinuousNumeric<number, number> | null = null;
    neutron_count_rate_g: any | null
    count_rate_width: number = 0
    count_rate_height: number = 0
    source_brush: d3.BrushBehavior<unknown> | null = null;
    brush_path: any;
    history_plot_range_msec = 120000;
    plot_scale_min_gamma = 4000; //cps
    plot_scale_min_neutron = 40; //cps

    constructor(props: CountHistoryPlotProps) {
        super(props)
        this.base = null

        this.state = {
            counthistory_log_scale: false,
        }

        this.resize = this.resize.bind(this);
        this.set_counthistory_log_scale = this.set_counthistory_log_scale.bind(this);

        this.updateRef = (element: HTMLDivElement) => {
            this.base = element;
            this.renderPlot();
        };
    }

    componentDidMount() {
        this.initPlot()
        this.renderPlot();
        window.addEventListener("resize", this.resize);
    }

    componentDidUpdate(prevProps: CountHistoryPlotProps, prevState: CountHistoryPlotState) {
        if (this.props.data !== prevProps.data) {
            this.renderPlot();
        }
        if (this.state.counthistory_log_scale !== prevState.counthistory_log_scale) {
            //Switch plot scale
            this.renderPlot();
        }
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.resize);
    }

    resize() {
        this.initPlot()
        this.renderPlot();
    }

    set_counthistory_log_scale(flag: boolean) {
        this.setState(state => ({
            counthistory_log_scale: flag
        }));
    }

    on_source_brush() {
        if (this.count_rate_x !== null && this.count_rate_y !== null) {
            var sel = d3.event.selection || this.count_rate_x.range();
            var times = sel.map(this.count_rate_x.invert, this.count_rate_x);
            times[0] = Math.round(times[0] / 1000) * 1000;
            times[1] = Math.round(times[1] / 1000) * 1000;
            if (d3.event.selection !== null) {
                if (this.previous_brush === undefined) {
                    this.props.onSelectRange(times[0], times[1])
                    this.previous_brush = times;
                } else {
                    if (this.previous_brush[0] !== times[0] || this.previous_brush[1] !== times[1]) {
                        this.props.onSelectRange(times[0], times[1])
                        this.previous_brush = times;
                    }
                }
            } else {
                if (this.previous_brush !== undefined) {
                    this.props.onSelectRange(null, null);
                    this.previous_brush = undefined;
                    if (this.source_brush !== null)
                        this.brush_path.call(this.source_brush.move, null);
                }
            }
        }
    }

    initPlot() {
        const width = this.base!.clientWidth - 20;  // subtract 20 to compensate for margins
        const height = this.base!.clientHeight - 20;

        this.count_rate_width = width -
            this.plot_margin.left - this.plot_margin.right;
        this.count_rate_height = height -
            this.plot_margin.top - this.plot_margin.bottom;

        d3.select(this.base!).select("svg").remove();

        const count_rate_svg = d3.select(this.base!).append("svg")
            .attr("width", `${width}px`)
            .attr("height", `${height}px`);

        //Neutron Plot
        this.neutron_count_rate_g = count_rate_svg.append('g').attr(
            'transform', 'translate(' + this.plot_margin.left + ',' +
            this.plot_margin.top + ')');

        this.neutron_count_rate_g.selectAll('.plot-axis').remove();
        this.neutron_count_rate_g.selectAll('path').remove();

        this.neutron_count_rate_x =
            d3.scaleTime().rangeRound([0, this.count_rate_width]);
        this.neutron_count_rate_y =
            d3.scaleLinear().rangeRound([this.count_rate_height,0]);

        //Gamma Plot
        this.count_rate_g = count_rate_svg.append('g').attr(
            'transform', 'translate(' + this.plot_margin.left + ',' +
            this.plot_margin.top + ')');

        this.count_rate_g.selectAll('.plot-axis').remove();
        this.count_rate_g.selectAll('path').remove();

        this.count_rate_x =
            d3.scaleTime().rangeRound([0, this.count_rate_width]);
        this.count_rate_y =
            d3.scaleLinear().rangeRound([this.count_rate_height, 0]);

        this.source_brush =
            d3.brushX().on('brush end', this.on_source_brush.bind(this));

        this.brush_path = this.count_rate_g.append('g')
            .attr('class', 'brush')
            .call(this.source_brush);
    }

    renderPlot() {
        if (this.count_rate_x !== null && this.count_rate_y !== null) {
            let max_all_x = 0;
            let min_plot_x = 0;

            //Plot Scale
            if (this.state.counthistory_log_scale) {
                this.count_rate_y = d3.scaleLog().rangeRound([this.count_rate_height, 0]);
                this.neutron_count_rate_y = d3.scaleLog().rangeRound([this.count_rate_height, 0]);
            } else {
                this.count_rate_y = d3.scaleLinear().rangeRound([this.count_rate_height, 0]);
                this.neutron_count_rate_y = d3.scaleLinear().rangeRound([this.count_rate_height, 0]);
            }

            //Find common x domain between gamma and neutron data
            if(this.props.neutronData !== null){
                //Neutron data exists
                if(this.neutron_count_rate_x !== null && this.neutron_count_rate_y !== null){
                    if(this.props.data.length > 0){
                        //Neutron and Gamma data exist
                        let max_gamma_x = d3.max(this.props.data, function (d) { return d.time }) || 0;
                        let min_gamma_x = d3.min(this.props.data, function (d) { return d.time }) || 0;

                        let max_neutron_x = d3.max(this.props.neutronData, function (d) { return d.time }) || 0;
                        let min_neutron_x = d3.min(this.props.neutronData, function (d) { return d.time }) || 0;

                        max_all_x = Math.max(max_gamma_x,max_neutron_x);
                        var min_all_x=0;
                        if(min_gamma_x > 0){
                            if(min_neutron_x > 0){
                                min_all_x = Math.min(min_gamma_x,min_neutron_x);
                            } else {
                                min_all_x = min_gamma_x;
                            }
                        } else {
                            if(min_neutron_x > 0){
                                min_all_x = min_neutron_x;
                            } else {
                                min_all_x = 0;
                            }
                        }

                        min_plot_x = min_all_x;

                        if(this.props.liveStream === true){
                            if(min_plot_x < max_all_x-this.history_plot_range_msec && min_plot_x > 0){
                                min_plot_x = max_all_x-this.history_plot_range_msec;
                            }
                        }

                        this.neutron_count_rate_x.domain([
                            min_plot_x,
                            max_all_x
                        ]);
                        this.count_rate_x = this.neutron_count_rate_x;

                    } else {
                        //Neutron data only
                        this.neutron_count_rate_x.domain([
                            d3.min(this.props.neutronData, function (d) { return d.time }) || 0,
                            d3.max(this.props.neutronData, function (d) { return d.time }) || 0
                        ]);
                        this.count_rate_x = this.neutron_count_rate_x;
                    }
                }

            } else {
                //Neutron data doesn't exist
                if(this.props.data.length > 0){
                    //Gamma data only
                    this.count_rate_x.domain([
                        d3.min(this.props.data, function (d) { return d.time }) || 0,
                        d3.max(this.props.data, function (d) { return d.time }) || 0
                    ]);
                    this.neutron_count_rate_x = this.count_rate_x;
                } else {
                    //NO VALID DATA

                }
            }
            this.props.setStartTime(min_plot_x);

/*
            const validData = this.props.data.filter(function (d) { return d.sum !== null }).filter(function (d) { return d.time >= min_plot_x});;
            this.count_rate_y.domain([
                d3.min(validData, function (d) { return d.sum }) || 0,
                d3.max(validData, function (d) { return d.sum / d.livetime }) || 0
            ]);
*/

            //Y axis limits
            var gamma_maxval_y = 0;
            var gamma_minval_y = 100000;

            const validData = this.props.data.filter(function (d) { return d.sum !== null }).filter(function (d) { return d.time >= min_plot_x});;
            this.count_rate_y.domain([
                d3.min(validData, function (d) { return d.sum / d.livetime}) || 0,
                d3.max(validData, function (d) { return d.sum / d.livetime }) || 0
            ]);

            //Find max and min of gamma
            if(this.count_rate_y.domain()[1] > gamma_maxval_y){
                gamma_maxval_y = this.count_rate_y.domain()[1];
            }
            if(this.count_rate_y.domain()[0] < gamma_minval_y){
                gamma_minval_y = this.count_rate_y.domain()[0];
            }


            //Set range for plots
            if (gamma_maxval_y < this.plot_scale_min_gamma){
                this.count_rate_y.domain([gamma_minval_y,this.plot_scale_min_gamma]);
            }
            const log_gamma_floor_val = 1e-1;
            if (this.state.counthistory_log_scale && gamma_minval_y === 0)  {
                this.count_rate_y.domain([log_gamma_floor_val,this.plot_scale_min_gamma]);
            }

            var xscale = this.count_rate_x;
            var yscale = this.count_rate_y;

            // clang-format off
            this.count_rate_g.selectAll('.plot-axis').remove();
            this.count_rate_g.selectAll('.replay-time').remove();
            this.count_rate_g.selectAll('path').remove();
            this.neutron_count_rate_g.selectAll('.neutron-plot-axis').remove();
            this.neutron_count_rate_g.selectAll('path').remove();

            //Gamma Plot
            this.count_rate_g.append('g')
                .attr('class', 'plot-axis')
                .attr('transform', 'translate(0,' + this.count_rate_height + ')')
                .call(d3.axisBottom(xscale).scale(xscale))

            this.count_rate_g.append('g')
                .attr('class', 'plot-axis')
                .call(d3.axisLeft(yscale).ticks(5,d3.format(",d")));
                //.call(d3.axisLeft(yscale).ticks(4).tickFormat(x => `${x.valueOf().toFixed(0)}`));

            this.count_rate_g.append('g')
                .attr('class', 'plot-axis')
                .append("text")

                // this makes it easy to centre the text as the transform is applied to the anchor
                .attr("text-anchor", "middle")

                // text is drawn off the screen top left, move down and out and rotate
                .attr("transform", "translate(-" + this.plot_margin.left * 0.7 + "," +
                    (this.count_rate_height / 2) + ")rotate(-90)")

                .attr("fill", 'olivedrab')
                .text(t("Gamma Count Rate",{ns:'trans'}));

            this.count_rate_g.append('path')
                .datum(this.props.data)
                .attr('fill', 'none')
                .attr('stroke', 'olivedrab')
                .attr('stroke-width', 1.5)
                .attr('d', d3.line()
                    .x(function (d: any) { return xscale(d.time) as number})
                    .defined(function (d: any) { return d.sum !== null; })
                    .defined(function (d: any) { return d.time > min_plot_x; })
                    .y(function (d: any) { return yscale(d.sum / d.livetime) as number })
                );

            //Neutron plot
            if(this.props.neutronData !== null){
                let validNeutronData = this.props.neutronData.filter(function (d) { return d.count !== null}).filter(function (d) { return d.time >= min_plot_x})
                const log_neutron_floor_val = log_gamma_floor_val;
                if(this.state.counthistory_log_scale){
                    validNeutronData.forEach(el => {
                        if(el.count === 0)
                            el.count = log_neutron_floor_val;
                    });
                }
                if(this.neutron_count_rate_x !== null && this.neutron_count_rate_y !== null){

                    this.neutron_count_rate_y.domain([
                        d3.min(validNeutronData, function (d) { return d.count / d.livetime }) || 0,
                        d3.max(validNeutronData, function (d) { return d.count / d.livetime }) || 0
                    ]);

                    if (this.neutron_count_rate_y.domain()[1] < 10){
                        var neutron_minval = this.neutron_count_rate_y.domain()[0];
                        if(!this.state.counthistory_log_scale && neutron_minval < 1){
                            neutron_minval = 0; //If linear scale and min val is <1
                        } else if (this.state.counthistory_log_scale) {
                            neutron_minval = log_neutron_floor_val;
                        }
                        this.neutron_count_rate_y.domain([neutron_minval,10]);
                    }

                    let neutron_xscale = this.neutron_count_rate_x;
                    let neutron_yscale = this.neutron_count_rate_y;

                    this.neutron_count_rate_g.append('g')
                        .attr('class', 'neutron-plot-axis')
                        .attr('transform', 'translate(0,' + this.count_rate_height + ')')
                        .call(d3.axisBottom(neutron_xscale).scale(neutron_xscale))


                    this.neutron_count_rate_g.append('g')
                        .attr('class', 'neutron-plot-axis')
                        .attr('transform', 'translate('+ this.count_rate_width + ',0)')
                        .call(d3.axisRight(neutron_yscale).ticks(5,d3.format(".1f")));

                    this.neutron_count_rate_g.append('g')
                        .attr('class', 'neutron-plot-axis')
                        .append("text")
                        .attr("text-anchor", "middle")  // this makes it easy to centre the text as the transform is applied to the anchor
                        .attr("transform", "translate("+ (this.count_rate_width+this.plot_margin.right*0.70) +","+(this.count_rate_height/2)+")rotate(90)")  // text is drawn off the screen top left, move down and out and rotate
                        .attr("fill",'steelblue')
                        .text(t("Neutron Count Rate",{ns:'trans'}));

                    if(this.state.counthistory_log_scale){
                        //log scale so remove zero points
                        this.neutron_count_rate_g.append('path')
                            .datum(validNeutronData)
                            .attr('fill', 'none')
                            .attr('stroke', 'steelblue')
                            .attr('stroke-width', 1.5)
                            .attr('d',d3.line()
                                .x(function (d: any) { return neutron_xscale(d.time) as number })
                                .y(function (d: any) { return neutron_yscale(d.count / d.livetime) as number })
                                .defined(function (d: any) { return d.count !== null; })
                                .defined(function (d: any) { return d.time > min_plot_x; })
                                .defined(function (d: any) { return d.count > log_neutron_floor_val; })
                        );
                    } else {
                        //Linear scale so plot zero points
                        this.neutron_count_rate_g.append('path')
                            .datum(validNeutronData)
                            .attr('fill', 'none')
                            .attr('stroke', 'steelblue')
                            .attr('stroke-width', 1.5)
                            .attr('d',d3.line()
                                .x(function (d: any) { return neutron_xscale(d.time) as number })
                                .y(function (d: any) { return neutron_yscale(d.count / d.livetime) as number })
                                .defined(function (d: any) { return d.count !== null; })
                                .defined(function (d: any) { return d.time > min_plot_x; })
                            );
                    }
                }
            }

            if (this.source_brush !== null) {
                if (this.previous_brush !== undefined) {
                    let xs = this.previous_brush.map(xscale);
                    if(xs[0] !== undefined && xs[1] !== undefined){
                        if (xs[0] < 0)
                            xs[0] = 0;

                        if (xs[1] > 0) {
                            this.brush_path.call(this.source_brush.move, xs);
                        } else {
                            this.previous_brush = undefined;
                            this.brush_path.call(this.source_brush.move, null);
                        }
                    }
                }
            }

            if (this.props.replayTime && !this.props.liveStream) {
                var pos = this.count_rate_x(this.props.replayTime);
                if (pos === undefined)
                    pos = 0;
                if (pos < 0)
                    pos = 0;
                if (pos > this.count_rate_width)
                    pos = this.count_rate_width;

                this.count_rate_g.append("line")
                    .attr('class', 'replay-time')
                    .attr("x1", pos)
                    .attr("y1", 0)
                    .attr("x2", pos)
                    .attr("y2", this.count_rate_height)
                    .style("stroke-width", 2)
                    .style("stroke", "red")
                    .style("fill", "none");
            }
        }
    }

    render() {
        //console.log('Render CountHistoryPlot @'+Date.now())
        let linearSelectClass;
        let logSelectClass;
        if (this.state.counthistory_log_scale) {
            linearSelectClass = "";
            logSelectClass = "active";
        } else {
            linearSelectClass = "active";
            logSelectClass = "";
        }
        return (
            <div className="Plot" style={{ display: 'flex', flex: '1', flexDirection: 'column' }}>
                <div className="header">
                    <span>{t('Count History',{ns:'trans'})}</span>
                    <div className="icon-bar" style={{ paddingLeft: '10px', paddingBottom: '2px' }}>
                        <div className={"axis-type " + linearSelectClass} onClick={() => this.set_counthistory_log_scale(false)}>{t('LINEAR',{ns:'trans'})}</div>
                        <div className={"axis-type " + logSelectClass} onClick={() => this.set_counthistory_log_scale(true)}>{t('LOG',{ns:'trans'})}</div>
                    </div>
                </div>
                <div className="plot" ref={this.updateRef} style={{ display: 'flex', flex: '1', touchAction:'none' }} />
            </div>
        )
    }
}
const MemoizedCountHistoryPlot = React.memo(CountHistoryPlot,countHistoryPlotPropsAreEqual)

function countHistoryPlotPropsAreEqual(prevProps:CountHistoryPlotProps,nextProps:CountHistoryPlotProps){
    let flag = true;
    if(prevProps.replayTime !== nextProps.replayTime){
        flag = false;
    }
    if(nextProps.data.length > 0){
        if(prevProps.data.length > 0){
            if(prevProps.data[prevProps.data.length-1].time !== nextProps.data[nextProps.data.length-1].time){
                flag = false;
            }
        } else {
            flag = false;
        }
    } else if(nextProps.neutronData !== null && nextProps.neutronData.length > 0){
        if(prevProps.neutronData !== null && prevProps.neutronData.length > 0){
            if(prevProps.neutronData[prevProps.neutronData.length-1].time !== nextProps.neutronData[nextProps.neutronData.length-1].time){
                flag = false;
            }
        } else {
            flag = false;
        }
    }
    return (flag);
}

//******************************************************************************
export interface SpectrumPlotProps { data: any[], title: string, dominantIsotope: string | null }
export interface SpectrumPlotState {
    spectrum_log_scale: boolean,
    show_spectrum_cursor: boolean,
    spectrum_transform: any | undefined,
    focus_location_i: any | undefined,
    focus_location_coord_x: any | undefined,
    focus_location_x0: any | undefined
}

export class SpectrumPlot extends React.Component<SpectrumPlotProps, SpectrumPlotState, {}> {
    base: HTMLDivElement | null
    updateRef: (element: HTMLDivElement) => void

    plot_margin = { top: 20, right: 5, bottom: 30, left: 50 };
    spectrum_x: d3.ScaleContinuousNumeric<number, number> | null = null;
    spectrum_y: d3.ScaleContinuousNumeric<number, number> | null = null;
    spectrum_g: any | null
    spectrum_width: number = 0
    spectrum_height: number = 0
    focus: any
    energy_scale: any[];
    min_channel_rate = 5e-3;

    strong_lines_list: strong_lines[] | undefined;

    cursor_mousedown_flag: boolean;

    constructor(props: SpectrumPlotProps) {
        super(props)
        this.base = null
        this.state = {
            spectrum_log_scale: false,
            show_spectrum_cursor: false,
            spectrum_transform: undefined,
            focus_location_i: undefined,
            focus_location_coord_x: undefined,
            focus_location_x0: undefined
        }

        this.energy_scale = [];
        for (var i = 0; i < 1024; ++i) {
            this.energy_scale.push(3000 / 1024.0 / 2 + 3000 / 1024.0 * i);
        }

        this.resize = this.resize.bind(this);

        this.updateRef = (element: HTMLDivElement) => {
            this.base = element;
            this.renderPlot();
        };
        this.set_spectrum_log_scale = this.set_spectrum_log_scale.bind(this);

        this.strong_lines_list = strong_lines_list;
        this.cursor_mousedown_flag = false;
        this.cursor_grab = this.cursor_grab.bind(this);
    }

    componentDidMount() {
        this.initPlot()
        this.renderPlot();
        window.addEventListener("resize", this.resize);
    }

    componentDidUpdate(prevProps: SpectrumPlotProps, prevState: SpectrumPlotState) {
        if (this.props.data !== prevProps.data) {
            this.renderPlot();
        }
        if (this.state.spectrum_log_scale !== prevState.spectrum_log_scale) {
            //Switch plot scale
            this.renderPlot();
        }
    }
    componentWillUnmount() {
        window.removeEventListener("resize", this.resize);
    }

    set_spectrum_log_scale(flag: boolean) {
        this.setState(state => ({
            spectrum_log_scale: flag
        }));
    }

    resize() {
        this.initPlot()
        this.renderPlot();
    }

    toggle_spectrum_cursor() {
        this.setState(state => ({
            show_spectrum_cursor: !state.show_spectrum_cursor
        }));
        this.renderPlot();
    }


    zoomed() {
        if (this.state.spectrum_transform !== undefined) {
            this.check_bounds_spectral_transform(d3.event.transform);
        } else {
            this.setState(() => ({
                spectrum_transform: d3.event.transform
            }));
        }
        this.renderPlot();
    }

    mouseclick() {
        //if(d3.event.defaultPrevented) return; //dragged
        if (this.spectrum_x != null) {
            var coord = d3.mouse(this.base!);

            var xscale = this.spectrum_x;
            if (this.state.spectrum_transform !== undefined) {
                xscale = this.state.spectrum_transform.rescaleX(this.spectrum_x);
            }
            const focus_location_coord_x = coord[0] - this.plot_margin.left;
            var x0 = xscale.invert(focus_location_coord_x);

            this.setState(() => ({
                focus_location_coord_x: focus_location_coord_x
            }));
            this.setState(() => ({
                show_spectrum_cursor: true
            }));
            this.setState(() => ({
                focus_location_x0: x0
            }));

            this.renderCursor();
        }
    };

    cursor_dragged() {
        //console.log(d3.event);
        if (this.spectrum_x !== null) {
            var dx = d3.event.dx;// XXX: ;
            var xscale = this.spectrum_x;
            if (this.state.spectrum_transform !== undefined) {
                xscale = this.state.spectrum_transform.rescaleX(this.spectrum_x);
            }

            var x0 = xscale.invert(this.state.focus_location_coord_x + dx);
            //console.log("cursor_drag x: "+this.focus_location_coord_x+" x0: "+x0);
            if (x0 >= 0 && x0 < 3000) {
                this.setState(() => ({
                    focus_location_x0: x0
                }));
                this.setState((state) => ({
                    focus_location_coord_x: state.focus_location_coord_x + dx
                }));

                this.renderCursor()
            }
        }
    }

    cursor_grab(flag:boolean){
        this.cursor_mousedown_flag = flag;
        this.renderCursor();
    }

    calculate_cursor_location() {
        let d = this.state.focus_location_x0;
        //var d:EnergyCountPair= {energy:this.state.focus_location_x0, count:0};
        let bisectEnergy = d3.bisector(function (d: any) { return d.energy; }).left;
        let spectra = this.props.data;

        if (spectra != null) {
            let i = bisectEnergy(spectra, this.state.focus_location_x0, 1),
                d0 = spectra[i - 1],
                d1 = spectra[i];
            d = this.state.focus_location_x0 - d0.energy > d1.energy - this.state.focus_location_x0 ? d1 : d0;

            this.setState({ focus_location_i: i});
        } else {
            this.setState({ focus_location_i: undefined });
        }

        return d;
    }

    check_bounds_spectral_transform(input_transform: any) {
        //This function tests the input transform to see if the resulting scale will fall
        //within the 0 to 3000 keV plot range.
        var tx = Math.min(0, Math.max(input_transform.x, this.spectrum_width - this.spectrum_width * input_transform.k));
        var test_transform = this.state.spectrum_transform;
        var test_xscale = test_transform.rescaleX(this.spectrum_x);

        if (test_xscale.domain()[0] >= 0 && test_xscale.domain()[1] < 3000) {
            //Accept this transform as the result is in-bounds
            //this.spectrum_transform = input_transform;
            this.setState(() => ({
                spectrum_transform: input_transform
            }));
        } else {
            //Result is out of bounds. Set to valid value
            //this.spectrum_transform.x = tx;
            var valid_transform = this.state.spectrum_transform;
            valid_transform.x = tx;
            this.setState(() => ({
                spectrum_transform: valid_transform
            }));
        }
    }

    find_strong_lines(dominant_isotope:string){
        var lines:number[] = [];
        var hyphenated_nuclide_name:string = "";
        if(this.strong_lines_list !== undefined){
            this.strong_lines_list.forEach((isotope)=>{
                if(isotope.name.match(dominant_isotope)){
                    lines = isotope.lines;
                    hyphenated_nuclide_name = isotope.name_hyphen;
                }
            });
        }
        return {hyphenated_nuclide_name, lines};
    }

    initPlot() {
        if (this.base === null)
            return;

        const width = this.base!.clientWidth - 20;  // subtract 20 to compensate for margins
        const height = this.base!.clientHeight - 20;

        this.spectrum_width = width -
            this.plot_margin.left - this.plot_margin.right;
        this.spectrum_height = height -
            this.plot_margin.top - this.plot_margin.bottom;

        d3.select(this.base!).select("svg").remove();

        const spectrum_svg = d3.select(this.base!).append("svg")
            .attr("width", `${width}px`)
            .attr("height", `${height}px`);

        const zoom = d3.zoom()
            .scaleExtent([1, 10])
            .translateExtent([[0, 0], [this.spectrum_width, this.spectrum_height]])
            .on("zoom", this.zoomed.bind(this));

        this.spectrum_g = spectrum_svg.append('g').attr(
            'transform', 'translate(' + this.plot_margin.left + ',' +
            this.plot_margin.top + ')');

        this.spectrum_g.selectAll('.plot-axis').remove();
        this.spectrum_g.selectAll('path').remove();

        this.spectrum_x =
            d3.scaleLinear().rangeRound([0, this.spectrum_width]);

        if ((this.spectrum_width > 0) && (this.spectrum_height > 0))
            this.spectrum_g.append("rect")
                .attr("class", "spectrum-overlay")
                .attr("fill", "transparent")
                .attr("width", this.spectrum_width)
                .attr("height", this.spectrum_height)
                .call(zoom.bind(this))
                .on("dblclick.zoom", null)
                .on("click", this.mouseclick.bind(this));
    }

    renderPlot() {
        //Plot Scale
        if (this.state.spectrum_log_scale) {
            this.spectrum_y =
                d3.scaleLog().rangeRound([this.spectrum_height, 0]);
        } else {
            this.spectrum_y =
                d3.scaleLinear().rangeRound([this.spectrum_height, 0]);
        }

        //Spectrum Plot
        var source_spectrum = this.props.data;

        if (this.spectrum_x !== null && this.spectrum_y !== null) {
            this.spectrum_x.domain(
                [d3.min(this.energy_scale),
                d3.max(this.energy_scale)]);
            this.spectrum_y.domain(
                [d3.min(source_spectrum, function (d) { return d.count }),
                d3.max(source_spectrum, function (d) { return d.count })]
            );

            let spectrum_maxval_y = 10;
            if(this.spectrum_y.domain()[1] > spectrum_maxval_y){
                spectrum_maxval_y = this.spectrum_y.domain()[1];
            }
            this.spectrum_y.domain(
                [d3.min(source_spectrum, function (d) { return d.count }),
                spectrum_maxval_y]
            );

            var xscale = this.spectrum_x;
            var yscale = this.spectrum_y;

            if (this.state.spectrum_transform !== undefined) {
                this.check_bounds_spectral_transform(this.state.spectrum_transform);
                xscale = this.state.spectrum_transform.rescaleX(this.spectrum_x);
            }

            // clang-format off
            this.spectrum_g.selectAll('g').remove();
            this.spectrum_g.selectAll('path').remove();
            this.spectrum_g.selectAll(".focus-line").remove();
            this.spectrum_g.selectAll(".focus-line-fat").remove();
            this.spectrum_g.selectAll(".focus-line-text").remove();
            this.spectrum_g.selectAll(".strong-lines").remove();
            this.spectrum_g.selectAll(".focus").remove();

            this.spectrum_g.append('g')
                .attr('class', 'plot-axis')
                .attr('transform', 'translate(0,' + this.spectrum_height + ')')
                .call(d3.axisBottom(xscale).scale(xscale));

            this.spectrum_g.append('g')
                .attr('class', 'plot-axis spectrum-axis')
                .call(d3.axisLeft(this.spectrum_y));

            this.spectrum_g.append('path')
                .datum(source_spectrum)
                .attr('class', 'spectrum-area')
                .attr('fill', 'none')
                .attr('stroke', 'olivedrab')
                .attr('stroke-width', 1.5)
                .attr('d', d3.line()
                    .defined((d: any) => {
                        return d.energy < xscale.domain()[1] && d.energy > xscale.domain()[0] && d.count > this.min_channel_rate;
                    })
                    .x((d: any) => { return xscale(d.energy) as number; })
                    .y((d: any) => { return yscale(d.count) as number; }));

            this.spectrum_g.append('g')
                .attr('class', 'plot-axis')
                .append("text")
                .attr("text-anchor", "middle")  // this makes it easy to centre the text as the transform is applied to the anchor
                .attr("transform", "translate(-" + this.plot_margin.left * 0.7 + "," + (this.spectrum_height / 2) + ")rotate(-90)")  // text is drawn off the screen top left, move down and out and rotate
                .attr("fill", 'olivedrab')
                .text(t("Counts Per Second",{ns:'trans'}));

            if(this.props.dominantIsotope != null){
                const result = this.find_strong_lines(this.props.dominantIsotope);
                const hyphenated_nuclide_name = result.hyphenated_nuclide_name;
                const lines = result.lines;
                if(lines.length > 0){
                    lines.forEach((line)=>{
                        if(this.spectrum_y !== null){
                            if(line < xscale.domain()[1] && line > xscale.domain()[0] && !isNaN(this.spectrum_y.domain()[1])){
                                this.spectrum_g.append("line")
                                        .attr('class', 'strong-lines')
                                        .attr("x1", xscale(line))
                                        .attr("y1", 0)
                                        .attr("x2", xscale(line))
                                        .attr("y2", this.spectrum_height)
                                        .style("stroke-width", 1)
                                        .style("stroke", "orange")
                                        .style("fill", "none");

                                this.spectrum_g.append("text")
                                    .attr('class', 'strong-lines')
                                    .attr("x", xscale(line))
                                    .attr("y", this.spectrum_y(this.spectrum_y.domain()[1]))
                                    .style("fill", "orange")
                                    .attr("dy", ".35em")
                                    .attr("font-size","9px")
                                    .text(hyphenated_nuclide_name);

                                this.spectrum_g.append("text")
                                    .attr('class', 'strong-lines')
                                    .attr("x", xscale(line))
                                    .attr("y", this.spectrum_y(this.spectrum_y.domain()[1]))
                                    .style("fill", "orange")
                                    .attr("dy", "1.35em")
                                    .attr("font-size","9px")
                                    .text(line+"keV");
                            }
                        }
                    });
                }
            }

            if (this.state.show_spectrum_cursor) {
                this.renderCursor();
            }

        }
    }

    renderCursor() {
        if (this.spectrum_x !== null && this.spectrum_y !== null) {
            var d = this.calculate_cursor_location();
            if (this.state.focus_location_i !== undefined) {

                this.spectrum_g.selectAll(".focus-line").remove();
                this.spectrum_g.selectAll(".focus-line-fat").remove();
                this.spectrum_g.selectAll(".focus-line-text").remove();
                //this.spectrum_g.selectAll(".strong-lines").remove();
                this.spectrum_g.selectAll(".focus").remove();

                var xscale = this.spectrum_x;
                if (this.state.spectrum_transform !== undefined) {
                    xscale = this.state.spectrum_transform.rescaleX(this.spectrum_x);
                }

                if (!isNaN(d.count)) {
                    this.focus = this.spectrum_g.append("g")
                        .attr("class", "focus")
                        .attr("transform", "translate(" + xscale(d.energy) + "," + this.spectrum_y(d.count) + ")")
                        .style("display", null);
                    this.focus.append("circle")
                        .attr("r", 4.5);


                    if (this.state.show_spectrum_cursor) {
                        if (d.energy < xscale.domain()[1] && d.energy > xscale.domain()[0]) {
                            this.focus.style("display", null);
                        } else {
                            this.focus.style("display", "none");
                        }
                    } else {
                        this.focus.style("display", "none");
                    }

                    let dragCursor = d3.drag()
                        .on("start", ()=>{this.cursor_grab(true)})
                        .on("end", ()=>{this.cursor_grab(false)})
                        .on("drag", this.cursor_dragged.bind(this));
                    this.focus.attr("transform", "translate(" + xscale(d.energy) + "," + this.spectrum_y(d.count) + ")");
                    let str = d.energy.toFixed(1) + "keV : " + d.count.toFixed(2) + "cps"

                    if (this.focus.style("display") !== "none") {
                        this.spectrum_g.append("line")
                            .attr('class', 'focus-line')
                            .attr("x1", xscale(d.energy))
                            .attr("y1", 0)
                            .attr("x2", xscale(d.energy))
                            .attr("y2", this.spectrum_height)
                            .style("stroke-width", 1)
                            .style("stroke", "steelblue")
                            .style("fill", "none");
                        let grab_cursor = "grab";
                        if(this.cursor_mousedown_flag){
                            grab_cursor = "grabbing";
                        }
                        this.spectrum_g.append("line")
                            .attr('class', 'focus-line-fat')
                            .attr("x1", xscale(d.energy))
                            .attr("y1", 0)
                            .attr("x2", xscale(d.energy))
                            .attr("y2", this.spectrum_height)
                            .style("stroke", "transparent")
                            .attr('stroke-width', 20)
                            .style("fill", "none")
                            .style("cursor",grab_cursor)
                            .call(dragCursor.bind(this));
                        this.spectrum_g.append("text")
                            .text(str)
                            .attr('class', 'focus-line-text')
                            .attr("x", xscale(d.energy))
                            .attr("y", 0)
                            .attr("dy", ".35em")
                            .attr("cursor","pointer")
                            .on("click", this.toggle_spectrum_cursor.bind(this));
                    }
                }
            }
        }

    }

    render() {
        //console.log('Render SpectrumPlot')
        let linearSelectClass;
        let logSelectClass;
        if (this.state.spectrum_log_scale) {
            linearSelectClass = "";
            logSelectClass = "active";
        } else {
            linearSelectClass = "active";
            logSelectClass = "";
        }
        return (
            <div className="Plot" style={{ display: 'flex', flex: '1', flexDirection: 'column' }}>
                <div className="header">
                    <span>{t(this.props.title,{ns:'trans'})}</span>
                    <div className="icon-bar" style={{ paddingLeft: '10px', paddingBottom: '2px' }}>
                        <div className={"axis-type " + linearSelectClass} onClick={() => this.set_spectrum_log_scale(false)}>{t('LINEAR',{ns:'trans'})}</div>
                        <div className={"axis-type " + logSelectClass} onClick={() => this.set_spectrum_log_scale(true)}>{t('LOG',{ns:'trans'})}</div>
                    </div>
                </div>
                <div className="plot" ref={this.updateRef} style={{ display: 'flex', flex: '1', touchAction:'none' }} />
            </div>
        )
    }
}

const MemoizedSpectrumPlot = React.memo(SpectrumPlot,spectrumPlotPropsAreEqual)

function spectrumPlotPropsAreEqual(prevProps:SpectrumPlotProps,nextProps:SpectrumPlotProps){
    const sumPrevProps = d3.sum(prevProps.data, function (d) { return d.count });
    const sumnextProps = d3.sum(nextProps.data, function (d) { return d.count });
    let flag = sumPrevProps === sumnextProps && prevProps.dominantIsotope === nextProps.dominantIsotope;
    return (flag);
}
